import { lazy, type ComponentType } from 'react';

const RELOAD_FLAG = 'pnl_chunk_reloaded_at';
/** Janela pra considerar que já tentamos recarregar (evita loop de reload). */
const RELOAD_COOLDOWN_MS = 30_000;

/**
 * `lazy` resiliente a deploy. Quando publicamos uma versão nova, os arquivos JS
 * ganham hash novo e os antigos deixam de existir. Quem estava com o app ABERTO
 * ainda aponta pros hashes velhos, então o primeiro clique numa aba code-split
 * estourava "Failed to fetch dynamically imported module" e a tela ficava no
 * erro do router (foi o que aconteceu na aba Indicações).
 *
 * Aqui a falha de import vira um reload único: o browser rebusca o index.html,
 * pega os hashes atuais e a navegação segue. O cooldown no sessionStorage evita
 * loop caso a falha seja de rede (aí o erro sobe normal e o router mostra).
 */
export function lazyWithReload<
  // `any` aqui é proposital: aceita componentes com QUALQUER assinatura de props
  // (ex.: AffiliatePage recebe `embedded`), preservando o tipo no retorno.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends ComponentType<any>,
>(factory: () => Promise<{ default: T }>) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      const last = Number(sessionStorage.getItem(RELOAD_FLAG) ?? 0);
      const recentlyReloaded = Date.now() - last < RELOAD_COOLDOWN_MS;
      if (!recentlyReloaded) {
        sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
        window.location.reload();
        // Promise pendente: o reload assume antes do React renderizar o erro.
        return await new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}
